import Handlebars from "handlebars";
import { get as lodashGet } from "lodash";
import { DateTime } from "luxon";
import { createElement } from "react";
import { renderToString } from "react-dom/server";
import { type Prisma } from "prisma-generated/browser";
import { type payment_method } from "prisma-generated/enums";
import { pillarIconComponents } from "shared/components/icons/PillarIcon";
import { type PillarIconName } from "shared/components/icons/pillarIconNames";
import { coAuthorBlockTemplateUtil } from "shared/components/template/coAuthorBlockTemplateUtil";
import { getPaymentDetailsByMethod } from "shared/components/template/extractPaymentCardTypeAndNumber";
import { formatAddressTemplateUtil } from "shared/components/template/formatAddressTemplateUtil";
import { renderTemplateUtil } from "shared/components/template/renderTemplateUtil";
import type { LuxonDateFormat } from "shared/datetime/dateFormats";
import { LuxonDateFormats } from "shared/datetime/dateFormats";
import { formatLuxonDate } from "shared/datetime/formatLuxonDate";
import { formatUsdString } from "shared/helpers/invoice/formatUsdString";
import { generateInitials } from "shared/helpers/profile/generateInitials";
import { getDisplayNameHelper } from "shared/helpers/profile/getDisplayNameHelper";
import { getFormattedDegreeData } from "shared/helpers/profile/getFormattedDegreeData";
import { splitFullName } from "shared/helpers/profile/splitFullName";
import { hslToHex } from "shared/helpers/theme/color-helpers";
import {
  type InvoiceDto,
  type InvoiceView,
  mapInvoiceDtoToInvoiceView,
} from "shared/mappers/database/accounting/invoice/invoice";
import { type CertificationDefinitionDto } from "shared/mappers/database/certification/certification-definition";
import {
  mapProfileDtoToProfileView,
  type ProfileDto,
  type ProfileView,
} from "shared/mappers/database/profile/profile";
import {
  mapSocietyDtoToSocietyView,
  type SocietyDto,
  type SocietyView,
} from "shared/mappers/database/society/society";
import { type SocietyTheme } from "shared/mappers/database/society/theme";
import {
  mapWorkflowInstanceDtoToWorkflowInstanceView,
  type WorkflowInstanceDto,
  type WorkflowInstanceView,
} from "shared/mappers/database/workflow/workflow-instance";

type HandlebarsTemplateHelper = {
  name: string;
  helper: Handlebars.HelperDelegate;
};

type RegisterHandleBarHelpersProps = {
  timeZone: string;
};

export const registerHandleBarHelpers = ({
  timeZone = "America/Chicago",
}: RegisterHandleBarHelpersProps) => {
  const helpersRaw: HandlebarsTemplateHelper[] = [
    {
      name: "concat",
      helper: (...args: unknown[]) => {
        return args.slice(0, -1).join("");
      },
    },
    {
      name: "todayISO",
      helper: () => DateTime.now().setZone(timeZone).toISODate(),
    },
    {
      name: "greaterThan",
      helper: function (arg1, arg2, options) {
        return arg1 > arg2 ? options.fn(this) : options.inverse(this);
      },
    },
    {
      name: "greaterThanOrEqual",
      helper: function (arg1, arg2, options) {
        return arg1 >= arg2 ? options.fn(this) : options.inverse(this);
      },
    },
    {
      name: "lessThan",
      helper: function (arg1, arg2, options) {
        return arg1 < arg2 ? options.fn(this) : options.inverse(this);
      },
    },
    {
      name: "lessThanOrEqual",
      helper: function (arg1, arg2, options) {
        return arg1 <= arg2 ? options.fn(this) : options.inverse(this);
      },
    },
    {
      name: "daysBetween",
      helper: function (date1, date2) {
        const dt1 = DateTime.fromISO(date1, { zone: timeZone });
        const dt2 = DateTime.fromISO(date2, { zone: timeZone });
        if (!dt1.isValid || !dt2.isValid) {
          return 0;
        }
        return Math.abs(dt2.diff(dt1, "days").days);
      },
    },
    {
      name: "ifEqualString",
      helper: function (arg1, arg2, options) {
        if (arg1 === arg2) {
          return options.fn(this); // Render the block
        } else {
          return options.inverse(this); // Render the else block
        }
      },
    },
    {
      name: "ifArrayHasObjectWithValue",
      helper: (array: any[], property: string, value: string, options) => {
        if (array?.some((item) => lodashGet(item, property) === value)) {
          return options.fn(this);
        } else {
          return options.inverse(this);
        }
      },
    },
    {
      name: "ifNotEqual",
      helper: function (arg1, arg2, options) {
        if (arg1 !== arg2) {
          return options.fn(this);
        } else {
          return options.inverse(this);
        }
      },
    },
    {
      name: "formatUsdString",
      helper: (text: string) => formatUsdString(Number(text)),
    },
    {
      name: "formatDate",
      helper: (iso: string) => {
        return formatLuxonDate(iso, LuxonDateFormats.D, timeZone);
      },
    },
    {
      name: "formatTimeRangeToDisplay",
      helper: (startTime: string, endTime: string, inputTimezone: string) => {
        if (!startTime || !endTime) {
          return "";
        }
        const start = DateTime.fromISO(startTime, {
          zone: inputTimezone ?? timeZone,
        }).toFormat("h:mma");
        const end = DateTime.fromISO(endTime, {
          zone: inputTimezone ?? timeZone,
        }).toFormat("h:mma");
        return `${start.toLowerCase()} - ${end.toLowerCase()}`;
      },
    },
    {
      name: "formatLuxonDate",
      helper: (
        date: string,
        format: LuxonDateFormat,
        inputTimezone?: string,
      ) => {
        if (!date) {
          return "";
        }
        const isoDate = DateTime.fromISO(date.toString(), {
          zone: inputTimezone ?? timeZone,
        });
        if (!isoDate.isValid) {
          return "";
        }

        return isoDate.toFormat(format);
      },
    },
    {
      name: "includes",
      helper: (
        subject: string | string[],
        valueOrValues: string | string[],
      ) => {
        // Case 1: subject is an array -> membership test (existing behavior)
        if (Array.isArray(subject)) {
          if (Array.isArray(valueOrValues)) {
            return valueOrValues.some((val) => subject.includes(val));
          }
          return subject.includes(valueOrValues);
        }
        // Case 2: subject is a string -> substring test
        if (typeof subject === "string") {
          if (Array.isArray(valueOrValues)) {
            return valueOrValues.some((val) =>
              val ? subject.toLowerCase().includes(val.toLowerCase()) : false,
            );
          }
          return valueOrValues
            ? subject.toLowerCase().includes(valueOrValues.toLowerCase())
            : false;
        }
        return false;
      },
    },
    {
      name: "hasTag",
      helper: (array: { tagName: string }[] | undefined, value: string) =>
        Array.isArray(array)
          ? array.some(
              (item) => item?.tagName?.toLowerCase() === value.toLowerCase(),
            )
          : false,
    },
    {
      name: "formatAddress",
      helper: function (this: unknown, ...args: unknown[]) {
        const options = args.pop() as Handlebars.HelperOptions & {
          hash: { classPrefix?: string };
        };
        const [candidate] = args;

        return formatAddressTemplateUtil({
          candidate,
          hasCandidate: !!candidate,
          context: this,
          classPrefix: options?.hash?.classPrefix,
        });
      },
    },
    {
      name: "themeToHex",
      helper: (
        societyTheme: SocietyTheme[],
        hueModify?: string,
        saturationModify?: string,
        lightnessModify?: string,
      ) => hslToHex(societyTheme, hueModify, saturationModify, lightnessModify),
    },
    {
      name: "isNotLastFiltered",
      helper: function (currentIndex, items, property, value) {
        const filtered = items.filter((item: any) => item[property] === value);
        return currentIndex < filtered.length - 1;
      },
    },
    {
      name: "getMembershipDescription",
      helper: function (type, description, options) {
        const validDescriptions = [
          "Lifetime Member",
          "Distinguished Member",
          "Member Emeritus",
        ];
        if (
          type === "DynamicMembership" &&
          validDescriptions.includes(description)
        ) {
          return options.fn(this);
        } else {
          return options.inverse(this);
        }
      },
    },
    {
      name: "getFormattedDegreeData",
      helper: (degree, value) => {
        return getFormattedDegreeData(degree, value);
      },
    },
    {
      name: "getDisplayNameHelper",
      helper: (profile: ProfileView, includeAffiliation?: boolean) =>
        getDisplayNameHelper(profile, includeAffiliation),
    },
    {
      name: "getInitials",
      helper: (profile: ProfileView) => {
        const { firstName, lastName } = splitFullName(
          getDisplayNameHelper(profile),
        );
        return generateInitials(firstName, lastName);
      },
    },
    {
      name: "getIcon",
      helper: (iconName: PillarIconName) => {
        const IconComponent = pillarIconComponents[iconName];
        if (!IconComponent) {
          throw new Error(`Icon "${iconName}" not found.`);
        }
        return renderToString(createElement(IconComponent));
      },
    },
    {
      name: "withArray",
      helper: (options: Handlebars.HelperOptions) => {
        // Create a new data frame so we don't mutate the incoming one
        const frame = Handlebars.createFrame(options.data);
        // Initialize the working array
        const workingArray: unknown[] = [];
        frame._myArray = workingArray; // still available via @_myArray for backwards compatibility

        // Expose _myArray directly on the context so templates can reference `_myArray` (without @)
        // without needing to know about the data frame. We create a shallow cloned context to avoid
        // leaking _myArray onto the original root object.
        const parentRoot = options.data?.root || {};
        const context = { ...parentRoot, _myArray: workingArray };

        return options.fn(context, { data: frame });
      },
    },
    {
      name: "arrayPush",
      helper: (
        options: Handlebars.HelperOptions & { hash: { value: unknown } },
      ) => {
        const valueToPush = options.hash.value;
        const frame = options.data as unknown as {
          root?: Record<string, unknown> & { _myArray?: unknown[] };
          _myArray?: unknown[];
        };
        // Prefer root._myArray if present (context exposure), else fall back to frame._myArray
        const targetArray =
          (frame.root && frame.root._myArray) || frame._myArray;
        if (Array.isArray(targetArray)) {
          targetArray.push(valueToPush);
        }
      },
    },
    {
      name: "arrayJoin",
      helper: (
        options: Handlebars.HelperOptions & { hash: { separator?: string } },
      ) => {
        const frame = options.data as unknown as {
          root?: Record<string, unknown> & { _myArray?: unknown[] };
          _myArray?: unknown[];
        };
        const separator = options.hash.separator ?? ", ";
        const targetArray =
          (frame.root && frame.root._myArray) || frame._myArray;
        return Array.isArray(targetArray) ? targetArray.join(separator) : "";
      },
    },
    {
      name: "formatPaymentDetails",
      helper: (additionalInfo: unknown, method: payment_method) => {
        return getPaymentDetailsByMethod(additionalInfo, method);
      },
    },
    {
      name: "assign",
      helper: function (
        varName: string,
        value: unknown,
        options: Handlebars.HelperOptions,
      ) {
        if (!options.data.root) {
          options.data.root = {};
        }
        options.data.root[varName] = value;
      },
    },
    {
      name: "toUpperCase",
      helper: (...props) => {
        return (props[0]?.toString() ?? "").toUpperCase();
      },
    },
    {
      name: "coAuthorBlock",
      helper: (...props) => {
        const options = props[props.length - 1] as Handlebars.HelperOptions;
        const data = props.length == 2 ? props[0] : options.data.root;

        const {
          coAuthorArrayPath,
          authorFullNamePath,
          authorFirstNamePath,
          authorLastNamePath,
          authorProfileIdPath,
          authorAffiliationPath,
          authorSeperator,
          profileNeedsStylingPath,
          profileNeedsStylingClassName,
          profileNeedsIndicatorBeforeName,
          profileNeedsIndicatorAfterName,
          legendSeperator,
          positionPath,
          blockClassName,
          output,
          disabledAffiliationSup,
          noDataMessage,
        } = options.hash;

        return coAuthorBlockTemplateUtil({
          coAuthorArrayPath,
          authorFullNamePath,
          authorFirstNamePath,
          authorLastNamePath,
          authorProfileIdPath,
          authorAffiliationPath,
          authorSeperator,
          profileNeedsStylingPath,
          profileNeedsStylingClassName,
          profileNeedsIndicatorBeforeName,
          profileNeedsIndicatorAfterName,
          legendSeperator,
          positionPath,
          blockClassName,
          output,
          noDataMessage,
          disabledAffiliationSup,
          data,
        });
      },
    },
  ];

  /**
   * push: helper that takes a "value" hash param and pushes it into the current array.
   * Usage in template: {{push value="FCSI"}}
   */
  // const push = ;

  /**
   * join: helper that joins all items in the array into a string, with a separator.
   * Usage in template: {{join separator=", "}}
   */
  // const join = ;
  // Handlebars.registerHelper("push", push);
  // Handlebars.registerHelper("join", join);

  helpersRaw.forEach(({ name, helper }) => {
    Handlebars.registerHelper(name, helper);
  });
};
export type StandardTemplateVariables = {
  society?: SocietyView;
  profile?: ProfileView;
  displayName?: string;
  invoice?: InvoiceView;
  workflowInstance?: WorkflowInstanceView;
  tokenUrl?: string;
  message?: string;
  logo?: string;
  additionalTemplateVariables?: Prisma.InputJsonValue;
  invoiceReceiptHtml?: string;
  certificationDefinition?: CertificationDefinitionDto;
};
export type GetStandardTemplateVariablesUtilProps = {
  society?: SocietyDto;
  profile?: ProfileDto;
  invoice?: InvoiceDto;
  workflowInstance?: WorkflowInstanceDto;
  tokenUrl?: string;
  message?: string;
  logoBase64?: string;
  logoUrl?: string;
  additionalTemplateVariables?: Prisma.InputJsonValue;
  invoiceReceiptHtmlTemplate?: string;
  certificationDefinition?: CertificationDefinitionDto;
  timeZone?: string;
};
export const getStandardTemplateVariablesUtil = ({
  profile,
  society,
  invoice,
  workflowInstance,
  tokenUrl,
  message,
  logoBase64,
  logoUrl,
  additionalTemplateVariables,
  invoiceReceiptHtmlTemplate,
  certificationDefinition,
  timeZone = "America/Chicago",
}: GetStandardTemplateVariablesUtilProps) => {
  registerHandleBarHelpers({ timeZone });
  const standardVariables: StandardTemplateVariables = {
    society: society ? mapSocietyDtoToSocietyView(society) : undefined,
    profile: profile ? mapProfileDtoToProfileView(profile) : undefined,
    displayName: profile ? getDisplayNameHelper(profile) : undefined,
    invoice: invoice ? mapInvoiceDtoToInvoiceView(invoice) : undefined,
    workflowInstance: workflowInstance
      ? mapWorkflowInstanceDtoToWorkflowInstanceView(workflowInstance)
      : undefined,
    tokenUrl,
    message,
    logo: logoBase64 || logoUrl,
    additionalTemplateVariables,
    certificationDefinition,
  };
  if (invoiceReceiptHtmlTemplate && invoice) {
    standardVariables.invoiceReceiptHtml = renderTemplateUtil({
      template: invoiceReceiptHtmlTemplate,
      templateData: standardVariables,
    });
  }
  return standardVariables;
};
